import { faClock } from "@fortawesome/pro-regular-svg-icons";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { Dropdown } from "@/components/Dropdown/Dropdown";
import { COMPANY_ONLY_SCORE_NOTE } from "@/components/LeadScoringPopover/LeadScoringPopover";
import { isUnsureScore } from "@/components/LeadScoringPopover/scoreTier";
import { TextArea } from "@/components/TextArea/TextArea";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { useUpdateLeadScoringResultMutation } from "@/pages/Private/redux/list/list.api";
import { useGetScoreQuery } from "@/pages/Private/redux/score/score.api";
import { useAnchoredTooltip } from "@/utils/useAnchoredTooltip";
import { classNames } from "@/utils/classNames";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { useCloseOnOutsideClick } from "@/utils/useCloseOnOutsideClick";

import { renderCurrentScoreResult } from "./renderCurrentScoreResult";
import { CompanyCategoryResults, PersonaCategoryResults } from "../../Apollo/schema/get-people";
import { FROM_TYPE } from "../../Contacts/schema/contacts";
import { FeaturesCodes } from "../../Users/enum/codes";
import { ListPerson } from "../schema/list";

/**
 * The lead's score pill and the change-score panel behind it.
 *
 * Extracted from `allColumns.tsx` (PREC-4074) so the pill's own branches can be tested on their
 * own; `LeadStatusCell` renders it once a scoring result exists.
 */
export const RenderOverallScore: FunctionComponent<{
	scoring?: PersonaCategoryResults | null;
	companyCategory?: CompanyCategoryResults | null;
	listPerson: ListPerson;
}> = ({ scoring, companyCategory, listPerson }) => {
	const { t } = useTranslation();
	const { data: score } = useGetScoreQuery();
	const isLeadScoringActive = useIsFeatureActive(FeaturesCodes.LEAD_SCORING);

	// No stored score selects no grade: a missing score is not a disqualification (PREC-4075).
	const [overallScore, setOverallScore] = useState<string | null>(
		scoring?.overallScore || companyCategory?.leadScore || null
	);

	const [selectedPersonaCategory, setSelectedPersonaCategory] = useState<{
		id: number;
		title: string;
	} | null>();

	const [selectedCompanyCategory, setSelectedCompanyCategory] = useState<{
		id: number;
		title: string;
	} | null>();

	useEffect(() => {
		if (
			listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.scoreCompanyCategory ||
			listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.result
		) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setSelectedCompanyCategory({
				id:
					listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.scoreCompanyCategory
						?.id || 0,
				title:
					listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.scoreCompanyCategory
						?.category ||
					listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.result ||
					"",
			});
		}

		if (
			listPerson?.prospect?.scorePersonaCategoryResult?.scorePersonaCategory ||
			listPerson?.prospect?.scorePersonaCategoryResult?.result
		) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setSelectedPersonaCategory({
				id: listPerson?.prospect?.scorePersonaCategoryResult?.scorePersonaCategory?.id || 0,
				title:
					listPerson?.prospect?.scorePersonaCategoryResult?.scorePersonaCategory?.category ||
					listPerson?.prospect?.scorePersonaCategoryResult?.result ||
					"",
			});
		}
	}, [listPerson]);

	const [isVisible, setIsVisible] = useState(false);
	const [reason, setReason] = useState("");
	const [updateLeadScoringResult, { isLoading }] = useUpdateLeadScoringResultMutation();

	const tooltipRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (!overallScore) {
			return;
		}

		await updateLeadScoringResult({
			id: listPerson.id,
			overallScore,
			scorePersonaCategoryId: selectedPersonaCategory?.id || 0,
			personaResult: selectedPersonaCategory?.title || "",
			scoreCompanyCategoryId: selectedCompanyCategory?.id || 0,
			companyResult: selectedCompanyCategory?.title || "",
			reason,
			fromType: FROM_TYPE.LIST,
			listId: listPerson.list.id,
		}).unwrap();

		setIsVisible(false);
		setReason("");
	};

	useCloseOnOutsideClick(tooltipRef, isVisible, setIsVisible);

	useAnchoredTooltip(triggerRef, tooltipRef, isVisible);

	const isCompanyOnly = !scoring && !!companyCategory;
	const scorePrefix = isCompanyOnly ? "C" : "P";

	const renderChangeResult = () => {
		return ReactDOM.createPortal(
			<div
				ref={tooltipRef}
				className="fixed flex flex-col z-100 p-4 gap-4 bg-white rounded-lg shadow-lg z-[199] min-w-[300px]"
				onClick={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<div className="flex flex-row gap-2">
					<div
						className={classNames(
							"cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-success border font-bold bg-background-success rounded-full justify-center items-center",
							overallScore === "1" ? "border-blue-action border-2" : "border-text-success"
						)}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setOverallScore("1");
						}}
					>
						{scorePrefix}1
					</div>
					<div
						className={classNames(
							"cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-warning border font-bold bg-background-warning rounded-full justify-center items-center",
							overallScore === "2" ? "border-blue-action border-2" : "border-text-warning"
						)}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setOverallScore("2");
						}}
					>
						{scorePrefix}2
					</div>
					<div
						className={classNames(
							"cursor-pointer w-[24px] h-[24px] border text-error text-xxs  font-bold bg-error-light rounded-full justify-center items-center flex",
							overallScore === "3" ? "border-blue-action border-2" : "border-error"
						)}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setOverallScore("3");
						}}
					>
						{scorePrefix}3
					</div>
					<div
						className={classNames(
							"cursor-pointer w-[24px] h-[24px] text-white text-xxs  font-bold bg-error rounded-full justify-center items-center flex",
							overallScore === "Disqualified" ? "border-blue-action border-2" : "border-error"
						)}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setOverallScore("Disqualified");
						}}
					>
						DQ
					</div>
				</div>
				<div className="flex flex-col gap-2">
					<div>
						<Dropdown
							data={[
								...(score?.prospectCompanyCategories?.map((category) => ({
									id: category.id || 0,
									title: `${category.category} (${category.type?.capitalizeString()})`,
								})) || []),
								...(score?.partnerCompanyCategories?.map((category) => ({
									id: category.id || 0,
									title: `${category.category} (${category.type?.capitalizeString()})`,
								})) || []),
								{
									id: 0,
									title: "Disqualified",
								},
								{
									id: 0,
									title: "Competitor",
								},
							]}
							defaultValue={
								selectedCompanyCategory
									? {
											id: selectedCompanyCategory.id,
											title: selectedCompanyCategory.title,
										}
									: undefined
							}
							floating={true}
							portal={true}
							handleSelect={(selected) => {
								setSelectedCompanyCategory({
									id: selected.id as number,
									title: selected.title,
								});
							}}
							label="Company category"
						/>
					</div>
					<div>
						<Dropdown
							data={[
								...(score?.prospectPersonaCategories?.map((category) => ({
									id: category.id || 0,
									title: `${category.category}` || "",
								})) || []),
								{
									id: 0,
									title: "Disqualified",
								},
							]}
							defaultValue={
								selectedPersonaCategory
									? {
											id: selectedPersonaCategory.id,
											title: selectedPersonaCategory.title,
										}
									: undefined
							}
							floating={true}
							portal={true}
							handleSelect={(selected) => {
								setSelectedPersonaCategory({
									id: selected.id as number,
									title: selected.title,
								});
							}}
							label="Persona category"
						/>
					</div>
				</div>
				<div>
					<TextArea
						handleChange={(e) => setReason(e.target.value)}
						label="Reason for change"
						name="reason"
						placeholder="Enter reason for this change..."
						showError={false}
						value={reason}
					/>
				</div>
				<div className="flex flex-row gap-2 justify-end">
					<Button
						color={ButtonColor.ACTION}
						title="Save"
						disabled={isLoading || !overallScore}
						onClick={handleSave}
					/>
				</div>
			</div>,
			document.body
		);
	};

	if (scoring?.overallScore === "1") {
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					scoring,
					score: "P1",
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if (scoring?.overallScore === "2") {
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					scoring,
					score: "P2",
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if (scoring?.overallScore === "3") {
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					scoring,
					score: "P3",
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if (
		isCompanyOnly &&
		companyCategory &&
		["1", "2", "3"].includes(companyCategory.leadScore ?? "")
	) {
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					// A company-only score gets the same card as a full one — the C in the
					// badge is what says the persona half is missing, so the caveat belongs
					// in the header note rather than glued onto the reasoning as an <em>.
					scoring: {
						leadScoringBreakdown: companyCategory?.leadScoringBreakdown,
						overallReasoning: companyCategory?.leadScoringReasoning,
					},
					note: COMPANY_ONLY_SCORE_NOTE,
					score: `C${companyCategory.leadScore}`,
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if (companyCategory?.result === "Unsure") {
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					// No criteria were scored, but the verdict still belongs in the card
					// rather than in a tooltip of its own.
					scoring: { overallReasoning: companyCategory?.reason },
					score: "?",
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if (
		companyCategory?.result === "Competitor" ||
		companyCategory?.result === "Disqualified" ||
		companyCategory?.companyCategory === "Disqualified"
	) {
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					// The company verdict is the whole reason, and it lives on the
					// company result rather than the persona one — hand it to the card
					// directly so it is framed like every other score.
					scoring: { overallReasoning: companyCategory?.reason },
					score: "DQ",
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if (isUnsureScore(scoring)) {
		// The engine could not judge the person — no organization (PREC-3965), no persona that
		// applies to the company (PREC-4074), a segment × persona cell with no criteria (PREC-4075).
		// Not a verdict, so the same "?" the company-level Unsure gets above, with the reason the
		// engine wrote.
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					scoring: { ...scoring, overallReasoning: scoring?.overallReasoning || scoring?.reason },
					score: "?",
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if (
		scoring?.result === "Disqualified" ||
		scoring?.result === "Competitor" ||
		scoring?.overallScore === "Disqualified" ||
		scoring?.result === "Unsure"
	) {
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					// A DQ lead gets the same card as a P1 lead — it used to drop
					// `scoring` here and fall back to a raw-HTML tooltip, so the two
					// read as different features. A verdict the engine reached before
					// scoring a criterion has no `overallReasoning`, only the
					// classifier's `reason`; the card renders whichever exists.
					scoring: { ...scoring, overallReasoning: scoring.overallReasoning || scoring.reason },
					score: "DQ",
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if ((scoring || companyCategory) && isLeadScoringActive === false) {
		// A company classified without lead scoring: nothing is going to score this lead, so the
		// clock below would wait forever (PREC-4075).
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					scoring: { overallReasoning: t("inbox.leadScoringNotActive") },
					score: "?",
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if (scoring || companyCategory) {
		return (
			<>
				{isVisible && renderChangeResult()}
				<Tooltip content={"Still ongoing"}>
					<div
						ref={triggerRef}
						className="cursor-pointer w-[24px] h-[24px] text-text-meta text-xxs  font-bold bg-border-light rounded-full justify-center items-center flex"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setIsVisible(!isVisible);
						}}
					>
						<Icon className="text-text-disabled  w-6 h-6 p-1" icon={faClock} />
					</div>
				</Tooltip>
			</>
		);
	}

	return <></>;
};
